if (!customElements.get('multicolumn-slider')) {
  customElements.define(
    'multicolumn-slider',
    class MulticolumnSlider extends HTMLElement {
      constructor() {
        super();
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
      }

      connectedCallback() {
        this.swiper = this.querySelector('.swiper');
        if (!this.swiper) return;

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderPagination = this.querySelector('.swiper-pagination');
        this.enableSlider = this.swiper.dataset.enableSlider === 'true';
        this.selectedIndex = this.selectedIndex;

        if (!this.enableSlider) return;
        this.initSlider();
      }

      disconnectedCallback() {
        if (Shopify.designMode) {
          document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        }
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index'), 10) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        if (!this.swiper || this.sliderInstance) return;

        const additionalModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];
        this.sliderOptions = {
          slidesPerView: 1,
          loop: true,
          effect: 'fade',
          fadeEffect: {
            crossFade: true,
          },
          on: {
            init: this.handleAfterInit.bind(this),
          },
        };

        const nextButton = this.sliderControls?.querySelector('.swiper-button-next');
        const previousButton = this.sliderControls?.querySelector('.swiper-button-prev');
        if (nextButton && previousButton) {
          this.sliderOptions.navigation = {
            nextEl: nextButton,
            prevEl: previousButton,
          };
        }

        if (this.sliderPagination) {
          this.sliderOptions.pagination = {
            el: this.sliderPagination,
            clickable: true,
          };
        }

        const autoplayDelay = Number.parseInt(this.swiper.dataset.autoplay, 10);
        if (autoplayDelay > 0) {
          this.sliderOptions.autoplay = {
            delay: autoplayDelay,
            disableOnInteraction: false,
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this.swiper, this.sliderOptions, additionalModules);
        this.sliderInstance.init();

        const slider = this.sliderInstance?.slider;
        if (!slider) return;

        slider.on('realIndexChange', this.handleSlideChange.bind(this));

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.handleBlockSelect);
        }

        this.querySelectorAll('motion-element[data-text]').forEach((motionTextElement) => {
          if (
            typeof motionTextElement.resetAnimation === 'function' &&
            !motionTextElement.closest('.swiper-slide-active')
          ) {
            motionTextElement.resetAnimation();
          }
        });

        this.selectedElement = slider.slides?.[slider.activeIndex];
        if (this.selectedElement) {
          this.onReady(this.selectedElement);
        }

        FoxTheme.a11y.getFocusableElements(this).forEach((element) => {
          if (element.classList.contains('swiper-button')) return;

          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            if (slide && slider.slides) {
              slider.slideToLoop(slider.slides.indexOf(slide));
            }
          });
        });
      }

      handleBlockSelect(event) {
        if (!this.swiper || !this.sliderInstance?.slider) return;
        if (event.detail.sectionId !== this.swiper.dataset.sectionId) return;

        const target = event.target;
        if (!(target instanceof HTMLElement) || target.dataset.type !== 'slide') return;

        const index = Number(target.dataset.index);
        if (Number.isNaN(index)) return;

        this.sliderInstance.slider.slideToLoop(index);
      }

      onReady(selectedElement) {
        if (!selectedElement || FoxTheme.config.motionReduced) return;

        selectedElement.querySelectorAll('motion-element').forEach((motionElement) => {
          if (typeof motionElement.refreshAnimation === 'function') {
            motionElement.refreshAnimation();
          }
        });
      }

      handleAfterInit() {
        const activeBullet = this.sliderControls?.querySelector('.swiper-pagination-bullet-active');
        if (!activeBullet) return;

        activeBullet.classList.remove('swiper-pagination-bullet-active');
        activeBullet.offsetHeight;
        activeBullet.classList.add('swiper-pagination-bullet-active');
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;
        this.updateControlsScheme(slides?.[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name !== 'selected-index' || oldValue === null || oldValue === newValue) return;

        const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
        const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

        fromElements.forEach((fromElement) => {
          fromElement.querySelectorAll('motion-element').forEach((motionElement) => {
            if (
              motionElement.hasAttribute('data-text') &&
              typeof motionElement.resetAnimation === 'function'
            ) {
              motionElement.resetAnimation();
            }
          });
        });

        toElements.forEach((toElement) => {
          setTimeout(() => {
            if (!toElement.classList.contains('swiper-slide-active')) return;

            toElement.querySelectorAll('motion-element').forEach((motionElement) => {
              if (typeof motionElement.refreshAnimation === 'function') {
                motionElement.refreshAnimation();
              }
            });
          });
        });
      }

      updateControlsScheme(activeSlide) {
        if (!this.sliderControls || !activeSlide) return;

        const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
          className.startsWith('color-')
        );
        classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));

        const colorScheme = activeSlide.dataset.colorScheme;
        if (colorScheme) {
          this.sliderControls.classList.add(colorScheme);
        }
      }
    }
  );
}
